import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type SubscriptionTier      = 'FREE' | 'PRO' | 'ENTERPRISE' | 'CUSTOM';

/**
 * Tiers buyable through Stripe-hosted Checkout. FREE needs no Stripe
 * relationship; CUSTOM is admin-assigned at an admin-set price and is not
 * publicly listed.
 */
export type PurchasableTier       = 'PRO' | 'ENTERPRISE';
export type EntitlementSource     = 'DEFAULT' | 'STRIPE' | 'MANUAL';
export type StripeSubscriptionStatus =
  | 'trialing' | 'active' | 'incomplete' | 'incomplete_expired'
  | 'past_due' | 'canceled' | 'unpaid' | 'paused';

export interface BillingIssue {
  reason: 'PAYMENT_FAILED' | 'SUBSCRIPTION_UNPAID' | 'SUBSCRIPTION_INCOMPLETE' | 'SUBSCRIPTION_PAUSED';
  since:  string;
}

/**
 * Server-computed billing view. `entitledTier` is authoritative — the client
 * never derives entitlement from a Stripe status or from a checkout redirect.
 */
export interface BillingSummary {
  entitledTier:       SubscriptionTier;
  entitlementSource:  EntitlementSource;
  status:             StripeSubscriptionStatus | null;
  currentPeriodEnd:   string | null;
  cancelAtPeriodEnd:  boolean;
  graceUntil:         string | null;
  billingIssue:       BillingIssue | null;
  hasStripeCustomer:  boolean;
  canUpgrade:         boolean;
}

@Injectable({ providedIn: 'root' })
export class BillingService {
  private readonly base = `${environment.apiUrl}/api/v1/billing`;

  constructor(private readonly http: HttpClient) {}

  /** Server-backed subscription state. The only source of entitlement truth. */
  getSubscription(): Observable<{ subscription: BillingSummary }> {
    return this.http.get<{ subscription: BillingSummary }>(`${this.base}/subscription`);
  }

  /**
   * Starts a hosted Checkout session.
   *
   * `tier` is the only field sent. The server maps it to an allowlisted price
   * and owns the return URLs — deliberately nothing else is client-supplied.
   */
  createCheckoutSession(tier: PurchasableTier): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.base}/checkout-session`, { tier });
  }

  /** Opens the Stripe-hosted portal for payment methods, invoices, and cancellation. */
  createPortalSession(): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.base}/portal`, {});
  }
}
